import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeProvider";
import { useContext, useState, useEffect } from 'react';
import { BundleContext } from '../../context/BundleProvider';
import { getExamDetails } from '../../utils/examDetails';
import OnboardingLayout from './OnboardingLayout';
import { useAnalytics } from '../../context/AnalyticsProvider';

const Exam = () => {
    const navigate = useNavigate();
    const { setSelectedBundle } = useContext(BundleContext);
    const { theme } = useContext(ThemeContext);
    const {
        trackOnboardingStep,
        trackExamSelection,
        trackDiagnosticStartClicked,
        trackDiagnosticSkipped,
    } = useAnalytics();
    const [selectedExam, setSelectedExam] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        trackOnboardingStep('exam_selection', {
            step_number: 1
        });
    }, [trackOnboardingStep]);

    const styles = {
        container: {
            minHeight: '100vh',
            
        },
        content: {
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '2rem 1rem',
            paddingTop: '6rem',
        },
        header: {
            textAlign: 'center',
            marginBottom: '3rem',
        },
        title: {
            fontSize: '2.5rem',
            fontWeight: '800',
            marginBottom: '1rem',
            color: theme.text.primary,
        },
        subtitle: {
            fontSize: '1.125rem',
            color: theme.text.secondary,
            fontWeight: '500',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: '1.6',
        },
        examGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.5rem',
            marginBottom: '3rem',
            maxWidth: '900px',
            margin: '0 auto 3rem auto',
        },
        examCard: (isSelected) => ({
            background: theme.dashboard.card.background,
            border: `2px solid ${isSelected ? theme.primary.blue[500] : theme.dashboard.card.border}`,
            borderRadius: '1rem',
            padding: '2rem 1.5rem',
            cursor: loading ? 'not-allowed' : 'pointer',
            position: 'relative',
            overflow: 'hidden',
            // Removed boxShadow, transform, and transition for simplicity
        }),
        examCardGlow: (isSelected) => ({
            display: 'none', // Remove background glow
        }),
        examContent: {
            position: 'relative',
            zIndex: 1,
            textAlign: 'center',
        },
        examName: {
            fontSize: '1.5rem',
            fontWeight: '700',
            color: theme.text.primary,
            marginBottom: '0.75rem',
        },
        examDetails: {
            color: theme.text.secondary,
            fontSize: '1rem',
            marginBottom: '1rem',
        },
        examStats: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))',
            gap: '0.75rem',
            marginTop: '1rem',
        },
        statItem: {
            textAlign: 'center',
        },
        statNumber: {
            fontSize: '1rem',
            fontWeight: '700',
            color: theme.primary.blue[600],
            display: 'block',
        },
        statLabel: {
            fontSize: '0.65rem',
            color: theme.text.tertiary,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontWeight: '500',
        },
        selectionBadge: (isSelected) => ({
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            width: '2rem',
            height: '2rem',
            borderRadius: '50%',
            background: isSelected 
                ? theme.primary.blue[500]
                : theme.dashboard.card.border,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // Removed transition and transform
            opacity: isSelected ? 1 : 0.3,
        }),
        checkmark: {
            color: theme.neutral.white,
            fontSize: '0.875rem',
            fontWeight: 'bold',
        },
        buttonContainer: {
            textAlign: 'center',
            marginBottom: '2rem',
        },
        helperText: {
            textAlign: 'center',
            color: theme.text.tertiary,
            fontSize: '0.875rem',
            marginTop: '1rem',
        },
        loadingOverlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `${theme.backgrounds.primary}80`,
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
        },
        loadingContent: {
            background: theme.dashboard.card.background,
            padding: '2rem',
            borderRadius: '1rem',
            textAlign: 'center',
            // Removed boxShadow
            border: `1px solid ${theme.dashboard.card.border}`,
        },
        loadingText: {
            color: theme.text.primary,
            fontSize: '1.125rem',
            fontWeight: '600',
            marginTop: '1rem',
        }
    };

    const handleSelect = async (exam) => {
        if (loading) return;
        setSelectedExam(exam);
        await trackExamSelection(exam.key, {
            selection_source: 'onboarding',
            exam_name: exam.name
        });
    };

    const handleStartAssessment = async () => {
        if (!selectedExam || loading) return;
        await trackDiagnosticStartClicked(selectedExam.key, {
            exam_name: selectedExam.name,
        });
        setLoading(true);
        try {
            await setSelectedBundle(selectedExam.key);
            setTimeout(() => {
                navigate('/onboarding-test', { state: { selectedExam: selectedExam } });
            }, 1000);
        } catch (error) {
            console.error('Error starting assessment:', error);
            setLoading(false);
        }
    };

    // Remove handleCardHover and all hover logic

    const examDetails = getExamDetails();

    return (
        <OnboardingLayout>
            <div style={styles.container}>
                <div style={styles.content}>
                    {/* Header */}
                    <div style={styles.header}>
                        <h1 style={styles.title}>
                            Choose Your Certification Exam
                        </h1>
                        <p style={styles.subtitle}>
                            Select your target certification and start with a comprehensive diagnostic assessment 
                            to identify your current knowledge level and create a personalized study plan.
                        </p>
                    </div>

                    {/* Exam Cards */}
                    <div style={styles.examGrid}>
                        {examDetails.map((exam) => {
                            const isSelected = selectedExam?.key === exam.key;
                            return (
                                <div
                                    key={exam.key}
                                    style={styles.examCard(isSelected)}
                                    onClick={() => handleSelect(exam)}
                                    // Removed onMouseEnter and onMouseLeave
                                >
                                    {/* Background glow */}
                                    <div style={styles.examCardGlow(isSelected)} />
                                    {/* Selection badge */}
                                    <div style={styles.selectionBadge(isSelected)}>
                                        <span style={styles.checkmark}>✓</span>
                                    </div>
                                    {/* Content */}
                                    <div style={styles.examContent}>
                                        <h3 style={styles.examName}>
                                            {exam.name}
                                        </h3>
                                        <p style={styles.examDetails}>
                                            {exam.description}
                                        </p>
                                        {/* Stats */}
                                        <div style={styles.examStats}>
                                            {exam.numQuestions > 0 && (
                                                <div style={styles.statItem}>
                                                    <span style={styles.statNumber}>{exam.numQuestions}</span>
                                                    <span style={styles.statLabel}>Questions</span>
                                                </div>
                                            )}
                                            {exam.numFlashcards > 0 && (
                                                <div style={styles.statItem}>
                                                    <span style={styles.statNumber}>{exam.numFlashcards}</span>
                                                    <span style={styles.statLabel}>Flashcards</span>
                                                </div>
                                            )}
                                            {exam.numArticles > 0 && (
                                                <div style={styles.statItem}>
                                                    <span style={styles.statNumber}>{exam.numArticles}</span>
                                                    <span style={styles.statLabel}>Articles</span>
                                                </div>
                                            )}
                                            {exam.numGlossary > 0 && (
                                                <div style={styles.statItem}>
                                                    <span style={styles.statNumber}>{exam.numGlossary}</span>
                                                    <span style={styles.statLabel}>Glossary</span>
                                                </div>
                                            )}
                                            {exam.numAcronyms > 0 && (
                                                <div style={styles.statItem}>
                                                    <span style={styles.statNumber}>{exam.numAcronyms}</span>
                                                    <span style={styles.statLabel}>Acronyms</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Action Button */}
                    <div style={styles.buttonContainer}>
                        {selectedExam && (
                            <>
                                <button 
                                    className="inline-flex items-center px-8 py-4 font-semibold rounded-lg hover:scale-105 hover:shadow-lg transition-all duration-200"
                                    style={{
                                        background: theme?.primary?.blue?.[500],
                                        color: theme?.neutral?.absolute_white,
                                        opacity: loading ? 0.6 : 1,
                                        cursor: loading ? 'not-allowed' : 'pointer'
                                    }}
                                    disabled={loading}
                                    onClick={handleStartAssessment}
                                >
                                    {loading ? 'Preparing Assessment...' : 'Start Free Diagnostic Assessment'}
                                </button>
                                {/* New Skip Assessment Button */}
                                <button 
                                    className="inline-flex items-center px-8 py-4 font-semibold rounded-lg hover:scale-105 hover:shadow-lg transition-all duration-200 ml-4"
                                    style={{
                                        border: `2px solid ${theme?.primary?.blue?.[500]}`,
                                        color: theme?.primary?.blue?.[500],
                                        background: 'transparent',
                                        cursor: loading ? 'not-allowed' : 'pointer'
                                    }}
                                    disabled={loading}
                                    onClick={() => {
                                        trackDiagnosticSkipped({ exam: selectedExam?.key });
                                        navigate('/prepare');
                                    }}
                                >
                                    Skip Assessment & Start Studying
                                </button>
                            </>
                        )}
                        {selectedExam && !loading && (
                            <div style={styles.helperText}>
                                <strong>{selectedExam.name}</strong> diagnostic assessment • Approximately 15-20 minutes
                            </div>
                        )}
                    </div>
                    {/* Additional Info */}
                    {!selectedExam && (
                        <div style={styles.helperText}>
                            Please select a certification exam to begin your diagnostic assessment
                        </div>
                    )}
                </div>
            </div>
            {/* Loading Overlay */}
            {loading && (
                <div style={styles.loadingOverlay}>
                    <div style={styles.loadingContent}>
                        {/* Removed spinner animation, use a static loader */}
                        <div style={{
                            width: '3rem',
                            height: '3rem',
                            border: `3px solid ${theme.dashboard.card.border}`,
                            borderRadius: '50%',
                            margin: '0 auto',
                            background: theme.primary.blue[500],
                        }} />
                        <div style={styles.loadingText}>
                            Preparing your personalized assessment...
                        </div>
                    </div>
                </div>
            )}
            {/* Removed <style jsx> block */}
        </OnboardingLayout>
    );
};

export default Exam;